import { buildVarianceSnapshotsFromSession } from "./mcAccountability";
import { mcGetItem, mcSetItem, mcSetCloudItem } from "./mcCloudSync";
import { enqueueNotificationsForEvent, processNotificationsForEvent } from "./mcNotifications";
import { determineConfiguredSeverity, loadResponseDesignConfig, responseTriggerApplies } from "./mcResponseDesign";
import { loadSites, normalizeSiteId } from "./mcOrgData";
import { buildResponseRoleEscalationPlan } from "./mcEscalationPolicy";

export const MC_RESPONSE_EVENTS_KEY = "mc-response-events";
export const MC_RESPONSE_EVENT = "mc-response-event";

const MAX_RESPONSE_EVENTS = 2000;

function cleanText(value = "") {
  return String(value ?? "").trim();
}

function numberValue(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function round2(value) {
  return Math.round(numberValue(value) * 100) / 100;
}

function slug(value = "") {
  return cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "unassigned";
}


function siteEscalationFor(siteName = "", createdAt = new Date().toISOString()) {
  const target = normalizeSiteId(siteName);
  const site = loadSites().find((item) => [item.siteId, item.name, item.storeKey, ...(Array.isArray(item.aliases) ? item.aliases : [])].some((value) => normalizeSiteId(value || "") === target)) || null;
  return { site, plan: buildResponseRoleEscalationPlan(site || {}, createdAt) };
}

function eventIdForSession(sessionId = "") {
  const cleanSessionId = cleanText(sessionId);
  return cleanSessionId ? `response:${cleanSessionId}` : `response:${Date.now()}`;
}

export function determineResponseSeverity(exposure = 0, flaggedProducts = 0, siteScope = "ALL") {
  return determineConfiguredSeverity(exposure, flaggedProducts, loadResponseDesignConfig(siteScope));
}

export function responseSeverityLabel(value = "") {
  const key = cleanText(value).toLowerCase();
  if (key === "critical") return "Critical";
  if (key === "high") return "High";
  if (key === "medium") return "Medium";
  return "Low";
}


export function responseStatusLabel(value = "") {
  const key = cleanText(value || "open").toLowerCase();
  if (key === "acknowledged") return "Acknowledged";
  if (key === "investigating") return "Investigating";
  if (key === "awaiting_verification") return "Verification Pending";
  if (key === "resolved") return "Resolved";
  if (key === "dismissed") return "Dismissed";
  if (key === "escalated") return "Escalated";
  return "Open";
}

export function createResponseTimelineEntry(action = "Updated", by = "System") {
  return {
    id: `timeline:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    action: cleanText(action) || "Updated",
    by: cleanText(by) || "System",
  };
}

export function buildResponseRecommendations(event = {}) {
  const severity = cleanText(event.severity || "low").toLowerCase();
  const flags = numberValue(event.flaggedProducts);
  const exposure = Math.abs(numberValue(event.exposure));
  const recommendations = [];

  if (severity === "critical") {
    recommendations.push("Recount flagged products immediately.");
    recommendations.push("Verify recent deliveries, transfers, adjustments, and waste records.");
    recommendations.push("Escalate to the responsible manager if the variance cannot be explained.");
  } else if (severity === "high") {
    recommendations.push("Review the highest-value flagged products first.");
    recommendations.push("Check recent receiving, transfer, and adjustment activity.");
    recommendations.push("Assign an owner to confirm the cause before the next count.");
  } else if (severity === "medium") {
    recommendations.push("Review the flagged products and confirm whether a recount is needed.");
    recommendations.push("Watch for repeat variance in the next count cycle.");
  } else {
    recommendations.push("Monitor this variance and close it if no action is required.");
  }

  if (flags >= 10) recommendations.push("Group the flagged products by ownership area to identify the likely process gap.");
  if (exposure >= 1000) recommendations.push("Capture a short investigation note before resolving this event.");
  if (Array.isArray(event.products) && event.products.length) {
    const top = event.products[0]?.product;
    if (top) recommendations.push(`Prioritize investigation of ${top}.`);
  }

  return [...new Set(recommendations)];
}

function normalizeTimeline(event = {}) {
  const existing = Array.isArray(event.timeline) ? event.timeline.filter(Boolean) : [];
  if (existing.length) return existing;
  return [createResponseTimelineEntry("Event created", event.countedBy || "System")];
}

export function normalizeResponseEvent(event = {}) {
  const status = cleanText(event.status || "open").toLowerCase() || "open";
  const base = {
    assignedTo: "",
    assignedAt: null,
    acknowledgedAt: null,
    investigatingAt: null,
    awaitingVerificationAt: null,
    escalatedAt: null,
    rootCause: "",
    investigationNotes: "",
    evidence: [],
    resolvedAt: null,
    resolvedBy: "",
    verificationRequired: false,
    verificationRole: "",
    verificationProfile: "",
    verificationStatus: "not_required",
    verifiedAt: null,
    verifiedBy: "",
    verificationNote: "",
    ...event,
    status,
  };
  base.timeline = normalizeTimeline(base);
  base.recommendedActions = Array.isArray(base.recommendedActions) && base.recommendedActions.length
    ? base.recommendedActions
    : buildResponseRecommendations(base);
  return base;
}

export function loadResponseEvents() {
  try {
    const parsed = JSON.parse(mcGetItem(MC_RESPONSE_EVENTS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(normalizeResponseEvent) : [];
  } catch (_) {
    return [];
  }
}

export function saveResponseEvents(events = []) {
  const cleanEvents = Array.isArray(events) ? events.filter(Boolean).map(normalizeResponseEvent).slice(0, MAX_RESPONSE_EVENTS) : [];
  const value = JSON.stringify(cleanEvents);
  try { mcSetItem(MC_RESPONSE_EVENTS_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_RESPONSE_EVENTS_KEY, value).catch(() => {}); } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(MC_RESPONSE_EVENT, { detail: { events: cleanEvents } }));
  } catch (_) {}
  return cleanEvents;
}

function topFlaggedProductsFromSnapshots(snapshots = []) {
  return [...snapshots]
    .filter((row) => row && (numberValue(row.variance) !== 0 || numberValue(row.exposure) !== 0))
    .sort((a, b) => Math.abs(numberValue(b.exposure)) - Math.abs(numberValue(a.exposure)))
    .slice(0, 100)
    .map((row) => ({
      productKey: row.productKey || slug(row.product),
      product: row.product || "Unnamed product",
      sku: row.sku || "",
      category: row.category || "Unassigned category",
      systemSoh: round2(row.systemSoh),
      counted: round2(row.counted),
      waste: round2(row.waste),
      unitCost: round2(row.unitCost),
      variance: round2(row.variance),
      exposure: round2(row.exposure),
      direction: row.direction || (numberValue(row.exposure) < 0 ? "loss" : numberValue(row.exposure) > 0 ? "gain" : "neutral"),
    }));
}

export function buildResponseEventFromSession(sessionRecord = {}, detailSnapshot = {}) {
  // P4.3 — Response Intelligence is a consumer of the completed canonical Count
  // Ledger only. sessionRecord is discovery/routing compatibility; it may not
  // supply lifecycle, progress, exposure, flags, products or completion truth.
  const meta = detailSnapshot?.meta && typeof detailSnapshot.meta === "object" ? detailSnapshot.meta : {};
  const ledgerLines = Array.isArray(detailSnapshot?.lines) ? detailSnapshot.lines : [];
  const sessionId = cleanText(meta.sessionId || sessionRecord.sessionId);
  if (!sessionId || !ledgerLines.length) return null;

  const status = cleanText(meta.status).toLowerCase();
  const completionMarked = status === "completed"
    || status === "section complete"
    || Boolean(meta.completedAt || meta.sectionCompletedAt);
  if (!completionMarked) return null;

  // A response may only be born from the frozen summary committed by the
  // canonical completion transaction. Never rebuild these facts from Sessions.
  const canonicalSummary = meta?.sessionSummary && typeof meta.sessionSummary === "object"
    ? meta.sessionSummary
    : null;
  if (!canonicalSummary) return null;

  const snapshots = buildVarianceSnapshotsFromSession(meta, detailSnapshot);
  const flaggedRows = snapshots.filter((row) => row && (numberValue(row.variance) !== 0 || numberValue(row.exposure) !== 0));
  const canonicalExposure = canonicalSummary.exposure ?? canonicalSummary.costExposure ?? canonicalSummary.finalExposure;
  const canonicalFlags = canonicalSummary.flaggedCount ?? canonicalSummary.flaggedItems ?? canonicalSummary.flaggedProducts;
  if (!Number.isFinite(Number(canonicalExposure)) || !Number.isFinite(Number(canonicalFlags))) return null;
  const flaggedProducts = Math.max(0, Number(canonicalFlags));
  const exposure = round2(canonicalExposure);
  if (!flaggedProducts && exposure === 0) return null;

  const completedAt = cleanText(meta.sectionCompletedAt || meta.completedAt);
  if (!completedAt) return null;
  const siteName = cleanText(meta.storeName || meta.store || meta.siteName || "Unknown site");
  const sectionId = cleanText(meta.sectionId || (meta.fullSite ? "full-count" : "unassigned"));
  const sectionName = cleanText(meta.sectionName || (meta.fullSite ? "Full Count" : "Unassigned"));
  const cycleId = cleanText(meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id);
  const cycleName = cleanText(meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || "Unassigned Cycle");
  const countedBy = cleanText(meta.lastEditedBy || meta.startedBy || meta.createdBy || "Unknown user");
  const categoryNames = Array.from(new Set([
    ...(Array.isArray(meta.categoryNames) ? meta.categoryNames : []),
    ...(Array.isArray(meta.countGroupNames) ? meta.countGroupNames : []),
  ].map((item) => cleanText(item)).filter(Boolean)));
  const categoryScopeLabel = cleanText(meta.categoryScopeLabel || (categoryNames.length ? categoryNames.join(" • ") : "All Count Groups"));
  const responseDesign = loadResponseDesignConfig(siteName);
  if (!responseTriggerApplies(exposure, flaggedProducts, responseDesign)) return null;
  const severity = determineConfiguredSeverity(exposure, flaggedProducts, responseDesign);
  const { plan: escalationPlan, site: responseSite } = siteEscalationFor(siteName, completedAt);
  // Preserve the initial severity alongside the frozen role thresholds. A
  // later correction must not silently re-arm or reroute this response chain.
  escalationPlan.responseSeverity = severity;

  return {
    id: eventIdForSession(sessionId),
    createdAt: completedAt,
    updatedAt: new Date().toISOString(),
    sessionId,
    cycleId,
    cycleName,
    siteName,
    siteId: responseSite?.siteId || "",
    siteKey: slug(siteName),
    sectionId: sectionId || "unassigned",
    sectionName: sectionName || "Unassigned",
    categoryScopeType: cleanText(meta.categoryScopeType || (categoryNames.length ? "selected" : "all")),
    categoryNames,
    countGroupNames: categoryNames,
    categoryScopeLabel,
    categoryScopeSource: cleanText(meta.categoryScopeSource || "count-groups"),
    countedBy,
    flaggedProducts,
    exposure,
    absExposure: Math.abs(exposure),
    severity,
    responseDesignVersion: 1,
    escalationPlan,
    responseRecipients: responseSite ? { siteManagerPersonId: responseSite.siteManagerPersonId || "", operationsManagerPersonId: responseSite.operationsManagerPersonId || "", ownerPersonId: responseSite.ownerPersonId || "" } : {},
    status: "open",
    source: "canonical_count_ledger",
    sourceRevision: Number(meta.lifecycleRevision || 0),
    sourceCompletedAt: completedAt,
    sourceSessionSummary: {
      exposure,
      flaggedProducts,
      completedLines: Number(canonicalSummary.completedLines || 0),
      totalLines: Number(canonicalSummary.totalLines || ledgerLines.length),
    },
    title: `${responseSeverityLabel(severity)} inventory variance`,
    signal: exposure < 0 ? "shortage" : exposure > 0 ? "surplus" : "variance",
    assignedTo: "",
    assignedAt: null,
    acknowledgedAt: null,
    investigatingAt: null,
    awaitingVerificationAt: null,
    escalatedAt: null,
    rootCause: "",
    investigationNotes: "",
    evidence: [],
    resolvedAt: null,
    resolvedBy: "",
    timeline: [createResponseTimelineEntry(`${responseSeverityLabel(severity)} event created`, countedBy || "System")],
    products: topFlaggedProductsFromSnapshots(flaggedRows),
  };
}

export function appendResponseEvent(event = {}) {
  if (!event?.id) return loadResponseEvents();
  const existing = loadResponseEvents();
  const previous = existing.find((item) => item?.id === event.id);
  const nextEvent = normalizeResponseEvent({
    ...previous,
    ...event,
    escalationPlan: previous?.escalationPlan?.version === 2 && previous?.sourceCompletedAt === event.sourceCompletedAt
      ? previous.escalationPlan : event.escalationPlan,
    status: previous?.status && previous.status !== "open" ? previous.status : (event.status || previous?.status || "open"),
    assignedTo: previous?.assignedTo ?? event.assignedTo ?? "",
    assignedAt: previous?.assignedAt ?? event.assignedAt ?? null,
    acknowledgedAt: previous?.acknowledgedAt ?? event.acknowledgedAt ?? null,
    investigatingAt: previous?.investigatingAt ?? event.investigatingAt ?? null,
    awaitingVerificationAt: previous?.awaitingVerificationAt ?? event.awaitingVerificationAt ?? null,
    escalatedAt: previous?.escalatedAt ?? event.escalatedAt ?? null,
    rootCause: previous?.rootCause ?? event.rootCause ?? "",
    investigationNotes: previous?.investigationNotes ?? event.investigationNotes ?? "",
    evidence: previous?.evidence ?? event.evidence ?? [],
    timeline: previous?.timeline ?? event.timeline,
    resolvedAt: previous?.resolvedAt ?? event.resolvedAt ?? null,
    resolvedBy: previous?.resolvedBy ?? event.resolvedBy ?? "",
    createdAt: previous?.createdAt || event.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  const next = [nextEvent, ...existing.filter((item) => item?.id !== event.id)]
    .sort((a, b) => new Date(b.createdAt || b.updatedAt || 0).getTime() - new Date(a.createdAt || a.updatedAt || 0).getTime());
  const saved = saveResponseEvents(next);
  try {
    enqueueNotificationsForEvent(nextEvent);
    Promise.resolve(processNotificationsForEvent(nextEvent.id, "Response Designed")).catch((error) => {
      console.warn("Automatic response delivery failed", error);
    });
  } catch (error) {
    console.warn("Response notification queue failed", error);
  }
  return saved;
}

export function appendResponseEventFromSession(sessionRecord = {}, detailSnapshot = {}) {
  const event = buildResponseEventFromSession(sessionRecord, detailSnapshot);
  if (!event) return loadResponseEvents();
  return appendResponseEvent(event);
}


export function recordResponseCorrection(eventId, correction = {}, actor = "System") {
  const cleanId = cleanText(eventId);
  if (!cleanId) return loadResponseEvents();
  const now = cleanText(correction.submittedAt) || new Date().toISOString();
  const next = loadResponseEvents().map((event) => {
    if (event?.id !== cleanId) return event;
    const originalExposure = round2(correction.originalExposure ?? event.exposure);
    const currentExposure = round2(correction.currentExposure);
    const recoveredValue = round2(correction.recoveredValue ?? (currentExposure - originalExposure));
    const flaggedProducts = Math.max(0, Number(correction.flaggedProducts ?? event.flaggedProducts ?? 0));
    const responseDesign = loadResponseDesignConfig(event.siteName || event.siteKey || "ALL");
    const severity = determineConfiguredSeverity(currentExposure, flaggedProducts, responseDesign);
    const triggerStillApplies = responseTriggerApplies(currentExposure, flaggedProducts, responseDesign);
    const verificationRequired = Boolean(responseDesign?.verification?.required);
    const status = triggerStillApplies ? (verificationRequired ? "awaiting_verification" : "open") : "resolved";
    const previousSeverity = cleanText(event.severity || "low").toLowerCase();
    const corrections = Array.isArray(event.corrections) ? event.corrections : [];
    const revision = {
      id: cleanText(correction.id) || `correction:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      submittedAt: now,
      correctedBy: cleanText(correction.correctedBy || actor) || "System",
      correctedByRole: cleanText(correction.correctedByRole),
      reason: cleanText(correction.reason),
      startedAt: cleanText(correction.startedAt),
      originalExposure,
      currentExposure,
      recoveredValue,
      changedProducts: Array.isArray(correction.changedProducts) ? correction.changedProducts : [],
      changedProductCount: Number(correction.changedProductCount || correction.changedProducts?.length || 0),
    };
    const verifierRole = responseDesign?.verification?.role || "operations_manager";
    const verifierProfile = responseDesign?.verification?.customProfile || "";
    const timeline = [
      ...normalizeTimeline(event),
      createResponseTimelineEntry(`Correction submitted by ${revision.correctedBy}`, revision.correctedBy),
      createResponseTimelineEntry(`${revision.changedProductCount} product${revision.changedProductCount === 1 ? "" : "s"} corrected · recovery ${recoveredValue >= 0 ? "+" : ""}R${Math.abs(recoveredValue).toLocaleString()}`, revision.correctedBy),
    ];
    if (previousSeverity !== severity) timeline.push(createResponseTimelineEntry(`Severity changed from ${responseSeverityLabel(previousSeverity)} to ${responseSeverityLabel(severity)}`, revision.correctedBy));
    if (status === "resolved") timeline.push(createResponseTimelineEntry("Response resolved after correction", revision.correctedBy));
    else if (verificationRequired) timeline.push(createResponseTimelineEntry(`Correction submitted for ${verifierRole.replace(/_/g, " ")} verification`, revision.correctedBy));
    else timeline.push(createResponseTimelineEntry("Correction submitted · response remains open", revision.correctedBy));
    return normalizeResponseEvent({
      ...event,
      exposure: currentExposure,
      absExposure: Math.abs(currentExposure),
      flaggedProducts,
      severity,
      status,
      products: Array.isArray(correction.products) ? correction.products : event.products,
      corrections: [...corrections, revision],
      lastCorrectedBy: revision.correctedBy,
      lastCorrectedAt: now,
      recoveredValueTotal: round2(numberValue(event.recoveredValueTotal) + recoveredValue),
      timeline,
      awaitingVerificationAt: status === "awaiting_verification" ? now : event.awaitingVerificationAt,
      verificationRequired,
      verificationRole: verifierRole,
      verificationProfile: verifierProfile,
      verificationStatus: status === "awaiting_verification" ? "pending" : (status === "resolved" ? "not_required" : event.verificationStatus),
      verifiedAt: null,
      verifiedBy: "",
      verificationNote: "",
      resolvedAt: status === "resolved" ? now : null,
      resolvedBy: status === "resolved" ? revision.correctedBy : "",
      updatedAt: now,
    });
  });
  return saveResponseEvents(next);
}


export function verifyResponseCorrection(eventId, decision = "approve", actor = "System", note = "") {
  const cleanId = cleanText(eventId);
  if (!cleanId) return loadResponseEvents();
  const cleanDecision = cleanText(decision).toLowerCase() === "reject" ? "reject" : "approve";
  const now = new Date().toISOString();
  const next = loadResponseEvents().map((event) => {
    if (event?.id !== cleanId) return event;
    const timeline = [...normalizeTimeline(event)];
    if (cleanDecision === "approve") {
      timeline.push(createResponseTimelineEntry(`Correction verified by ${cleanText(actor) || "System"}`, actor));
      return normalizeResponseEvent({
        ...event,
        status: "resolved",
        verificationStatus: "approved",
        verifiedAt: now,
        verifiedBy: cleanText(actor) || "System",
        verificationNote: cleanText(note),
        resolvedAt: now,
        resolvedBy: cleanText(actor) || "System",
        timeline,
        updatedAt: now,
      });
    }
    timeline.push(createResponseTimelineEntry(`Correction rejected by ${cleanText(actor) || "System"}${cleanText(note) ? ` · ${cleanText(note)}` : ""}`, actor));
    return normalizeResponseEvent({
      ...event,
      status: "open",
      verificationStatus: "rejected",
      verifiedAt: now,
      verifiedBy: cleanText(actor) || "System",
      verificationNote: cleanText(note),
      resolvedAt: null,
      resolvedBy: "",
      timeline,
      updatedAt: now,
    });
  });
  return saveResponseEvents(next);
}

export function updateResponseEventStatus(eventId, status = "open", actor = "System") {
  const cleanId = cleanText(eventId);
  if (!cleanId) return loadResponseEvents();
  const allowed = new Set(["open", "acknowledged", "investigating", "awaiting_verification", "resolved", "dismissed", "escalated"]);
  const cleanStatus = allowed.has(cleanText(status).toLowerCase()) ? cleanText(status).toLowerCase() : "open";
  const now = new Date().toISOString();
  const next = loadResponseEvents().map((event) => {
    if (event?.id !== cleanId) return event;
    const previousStatus = cleanText(event.status || "open").toLowerCase();
    const timeline = previousStatus === cleanStatus
      ? normalizeTimeline(event)
      : [...normalizeTimeline(event), createResponseTimelineEntry(`Status changed to ${responseStatusLabel(cleanStatus)}`, actor)];
    return normalizeResponseEvent({
      ...event,
      status: cleanStatus,
      timeline,
      acknowledgedAt: cleanStatus === "acknowledged" ? (event.acknowledgedAt || now) : event.acknowledgedAt,
      investigatingAt: cleanStatus === "investigating" ? (event.investigatingAt || now) : event.investigatingAt,
      awaitingVerificationAt: cleanStatus === "awaiting_verification" ? (event.awaitingVerificationAt || now) : event.awaitingVerificationAt,
      escalatedAt: cleanStatus === "escalated" ? (event.escalatedAt || now) : event.escalatedAt,
      resolvedAt: cleanStatus === "resolved" ? (event.resolvedAt || now) : (cleanStatus === "dismissed" ? event.resolvedAt : null),
      resolvedBy: cleanStatus === "resolved" ? cleanText(actor || event.resolvedBy || "System") : event.resolvedBy,
      updatedAt: now,
    });
  });
  return saveResponseEvents(next);
}

export function updateResponseEventDetails(eventId, patch = {}, actor = "System") {
  const cleanId = cleanText(eventId);
  if (!cleanId) return loadResponseEvents();
  const now = new Date().toISOString();
  const next = loadResponseEvents().map((event) => {
    if (event?.id !== cleanId) return event;
    const changes = [];
    if (Object.prototype.hasOwnProperty.call(patch, "assignedTo") && cleanText(patch.assignedTo) !== cleanText(event.assignedTo)) changes.push(`Assigned to ${cleanText(patch.assignedTo) || "Unassigned"}`);
    if (Object.prototype.hasOwnProperty.call(patch, "rootCause") && cleanText(patch.rootCause) !== cleanText(event.rootCause)) changes.push(`Root cause set to ${cleanText(patch.rootCause) || "Unassigned"}`);
    if (Object.prototype.hasOwnProperty.call(patch, "investigationNotes") && cleanText(patch.investigationNotes) !== cleanText(event.investigationNotes)) changes.push("Investigation notes updated");
    if (Object.prototype.hasOwnProperty.call(patch, "evidence") && patch.evidence !== event.evidence) changes.push("Evidence updated");
    const timeline = changes.length
      ? [...normalizeTimeline(event), ...changes.map((action) => createResponseTimelineEntry(action, actor))]
      : normalizeTimeline(event);
    const assignedChanged = Object.prototype.hasOwnProperty.call(patch, "assignedTo") && cleanText(patch.assignedTo) !== cleanText(event.assignedTo);
    return normalizeResponseEvent({
      ...event,
      ...patch,
      assignedAt: assignedChanged && cleanText(patch.assignedTo) ? now : (assignedChanged ? null : event.assignedAt),
      timeline,
      updatedAt: now,
    });
  });
  return saveResponseEvents(next);
}

export function addResponseEventEvidence(eventId, evidence = {}, actor = "System") {
  const cleanId = cleanText(eventId);
  if (!cleanId) return loadResponseEvents();
  const now = new Date().toISOString();
  const next = loadResponseEvents().map((event) => {
    if (event?.id !== cleanId) return event;
    const cleanEvidence = {
      id: `evidence:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      type: cleanText(evidence.type || "comment"),
      text: cleanText(evidence.text || evidence.note || ""),
      filename: cleanText(evidence.filename || ""),
      createdAt: now,
      createdBy: cleanText(actor || "System"),
    };
    const evidenceList = [...(Array.isArray(event.evidence) ? event.evidence : []), cleanEvidence];
    const timeline = [...normalizeTimeline(event), createResponseTimelineEntry(`${cleanEvidence.type === "comment" ? "Comment" : "Evidence"} added`, actor)];
    return normalizeResponseEvent({ ...event, evidence: evidenceList, timeline, updatedAt: now });
  });
  return saveResponseEvents(next);
}

export function buildResponseEventSummary(events = []) {
  const safeEvents = Array.isArray(events) ? events.filter(Boolean) : [];
  const open = safeEvents.filter((event) => !["resolved", "dismissed"].includes(cleanText(event.status).toLowerCase()));
  const critical = open.filter((event) => event.severity === "critical");
  const high = open.filter((event) => event.severity === "high");
  const medium = open.filter((event) => event.severity === "medium");
  const low = open.filter((event) => event.severity === "low");
  const investigating = safeEvents.filter((event) => event.status === "investigating");
  const resolved = safeEvents.filter((event) => event.status === "resolved");
  const totalExposure = round2(open.reduce((sum, event) => sum + numberValue(event.exposure), 0));
  const highest = [...open].sort((a, b) => Math.abs(numberValue(b.exposure)) - Math.abs(numberValue(a.exposure)))[0] || null;
  const oldestOpenMinutes = open.length ? Math.max(...open.map((event) => getResponseEventAgeMinutes(event))) : 0;
  const averageInvestigatingMinutes = investigating.length
    ? Math.round(investigating.reduce((sum, event) => sum + getResponseEventAgeMinutes(event), 0) / investigating.length)
    : 0;
  const resolvedToday = resolved.filter((event) => {
    const resolvedTime = parseResponseTimestamp(event.resolvedAt || event.updatedAt || event.createdAt);
    return new Date(resolvedTime).toDateString() === new Date().toDateString();
  });
  const averageResolvedMinutes = resolvedToday.length
    ? Math.round(resolvedToday.reduce((sum, event) => sum + getResponseEventAgeMinutes(event), 0) / resolvedToday.length)
    : 0;
  return {
    events: safeEvents,
    open,
    critical,
    high,
    medium,
    low,
    investigating,
    resolved,
    resolvedToday,
    totalExposure,
    highest,
    oldestOpenMinutes,
    averageInvestigatingMinutes,
    averageResolvedMinutes,
  };
}


export const RESPONSE_SLA = { critical: 30, high: 180, medium: 480, low: 1440 };

export function parseResponseTimestamp(value) {
  if (value === null || value === undefined || value === '') return Date.now();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 1000000000) return numeric;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Date.now();
}

export function appendTimelineEntry(event = {}, action = '', user = '', metadata = {}) {
  return {
    ...event,
    timeline: [
      ...(Array.isArray(event.timeline) ? event.timeline : []),
      {
        id: `timeline:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
        timestamp: new Date().toISOString(),
        action: cleanText(action) || 'Updated',
        by: cleanText(user) || 'System',
        user: cleanText(user) || 'System',
        metadata,
      },
    ],
  };
}

export function getResponseEventAgeMinutes(event = {}) {
  const end = event.resolvedAt ? parseResponseTimestamp(event.resolvedAt) : Date.now();
  const start = parseResponseTimestamp(event.createdAt || event.updatedAt);
  return Math.max(0, Math.floor((end - start) / 60000));
}

export function getResponseSlaLimitMinutes(event = {}) {
  if (event.escalationPlan?.version === 2) {
    const minutes = Number(event.escalationPlan.ownerAfterMinutes ?? event.escalationPlan.operationsAfterMinutes);
    if (Number.isFinite(minutes) && minutes >= 0) return minutes;
  }
  const config = loadResponseDesignConfig(event.siteName || event.siteKey || "ALL");
  const severity = String(event.severity || 'low').toLowerCase();
  const rule = config.escalation[severity] || config.escalation.low;
  return rule.ownerAfterMinutes || rule.operationsAfterMinutes || RESPONSE_SLA[severity] || RESPONSE_SLA.low;
}

export function isResponseSlaBreached(event = {}) {
  const status = cleanText(event.status || 'open').toLowerCase();
  if (['resolved', 'dismissed'].includes(status)) return false;
  return getResponseEventAgeMinutes(event) > getResponseSlaLimitMinutes(event);
}

export function formatResponseDuration(minutes = 0) {
  const safe = Math.max(0, Math.floor(numberValue(minutes)));
  if (safe < 60) return `${safe}m`;
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours < 24) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}
